/**
 * Port of the `Common.Format` helpers in wwwroot/js/app/common.js, which every
 * page used for rendering dates and names.
 */

/** `Common.Format.isNullOrWhitespace` */
export function isNullOrWhitespace(input: string | null | undefined): boolean {
  return !input || input.trim().length === 0
}

/** `Common.Format.CapitalizedText` — upper-cases the first letter of each word. */
export function capitalizedText(text: string): string {
  return text.replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * `Common.Format.ConvertJsonDate` — "02 January 2024", or "-" for a missing
 * value.
 *
 * The locale tag is 'en-ID', which is what the original passed. It is not a
 * standard pairing, and Intl resolves it to English with Indonesian regional
 * defaults; kept verbatim so dates render exactly as before.
 */
export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-ID', { day: '2-digit', month: 'long', year: 'numeric' })
}

/** `Common.Format.ConvertJsonDateToDateTime` — as above, plus hh:mm:ss. */
export function formatDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/**
 * `Common.Format.MaskEmail` — "jdoe@x.com" -> "j***e@x.com".
 *
 * The original indexed into the local part without checking its length, so a
 * one-character local part produced `"*".repeat(-1)` and threw. Guarded here;
 * short addresses are returned unmasked rather than crashing the page.
 */
export function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@')
  if (!localPart || !domain) return email
  if (localPart.length < 3) return email
  const masked =
    localPart.charAt(0) + '*'.repeat(localPart.length - 2) + localPart.charAt(localPart.length - 1)
  return `${masked}@${domain}`
}

/** `Common.Application.scrollToElement` */
export function scrollToElement(selector: string): void {
  document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
